import {
    Alert,
    AlertTitle,
    Button,
    Collapse,
    Dialog,
    LinearProgress,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { DropPaper } from '../drops/DropPaper'
import { getAccountBalance } from '../helpers/Api'
import { sleep } from '../helpers/FormatLinks'
import { getBoostAction } from '../helpers/WaxApi'
import { useSharedState } from '../waxplorer/Waxplorer'
import MainButton from '../common/util/input/MainButton'

export const CraftBalanceWithdrawalDialog = ({
    open,
    setOpen,
    collectionName = null,
}) => {
    const [state] = useSharedState()

    const activeUser = state?.activeUser

    const userName = activeUser?.accountName
        ? state?.activeUser.accountName
        : null

    const [balances, setBalances] = useState(null)
    const [isWithdrawing, setIsWithdrawing] = useState(false)

    const [error, setError] = useState(null)
    const [transactionId, setTransactionId] = useState(null)

    const withdraw = async () => {
        setError(null)
        setTransactionId(null)
        setIsWithdrawing(true)

        const withdrawActions = [getBoostAction(activeUser)]

        const withdrawData = {
            tokens: balances,
        }

        if (collectionName) {
            withdrawData.authorized_account = userName
            withdrawData.collection_name = collectionName
            withdrawData.recipient = userName
        } else {
            withdrawData.account = userName
        }

        const withdrawAction = {
            account: 'nfthivecraft',
            name: collectionName ? 'withdrawcol' : 'withdraw',
            authorization: [
                {
                    actor: userName,
                    permission: activeUser.requestPermission
                        ? activeUser.requestPermission
                        : 'active',
                },
            ],
            data: withdrawData,
        }

        withdrawActions.push(withdrawAction)
        let withdrawError = null
        let withdrawTransactionId = null

        try {
            const withdrawResult = await activeUser.session.transact(
                {
                    actions: withdrawActions,
                },
                {
                    expireSeconds: 300,
                    blocksBehind: 0,
                },
            )

            withdrawTransactionId = withdrawResult?.response?.transaction_id
        } catch (e) {
            withdrawError = e.message
            setError(e.message)
        }

        if (withdrawTransactionId) {
            setTransactionId(withdrawTransactionId)
        }
        await sleep(2000)
        fetchUserBalance()
        setIsWithdrawing(false)
        return
    }

    const parseBalance = (res) => {
        setBalances(res)
    }

    const fetchUserBalance = async () => {
        getAccountBalance(
            collectionName ? collectionName : userName,
            state,
        ).then(parseBalance)
    }

    useEffect(() => {
        if (typeof window !== 'undefined' && userName !== null) {
            fetchUserBalance()
        }
    }, [userName])

    const close = () => {
        if (!isWithdrawing) {
            setError(null)
            setTransactionId(null)
            setOpen(false)
        }
    }

    return (
        <Dialog open={open} onClose={() => close()} maxWidth="md" fullWidth>
            <DropPaper title="Your Crafting Balance" noMargin>
                {!userName ? (
                    <div>You need to be logged in to use this feature!</div>
                ) : null}
                {userName && balances === null ? (
                    <div>
                        <div className="text-lg italic">
                            Fetching your balance...
                        </div>
                        <LinearProgress />
                    </div>
                ) : null}
                {userName && balances !== null && balances.length === 0 ? (
                    <>
                        <div className="text-center">
                            {collectionName
                                ? `The collection ${collectionName} doesn't `
                                : `You don't `}{' '}
                            have any tokens in the nfthivecraft contract
                        </div>
                        <div className="flex justify-center">
                            <div className="w-36 m-5">
                                <MainButton
                                    fullWidth
                                    color="danger"
                                    onClick={() => {
                                        close()
                                    }}
                                >
                                    {' '}
                                    Close
                                </MainButton>
                            </div>
                        </div>
                    </>
                ) : null}
                {userName && balances !== null && balances.length > 0 ? (
                    <div>
                        <div>Your tokens in the nfthivecraft contract:</div>
                        {balances.map((balance) => (
                            <div className="my-2 font-mono">{balance}</div>
                        ))}
                        <div className="flex justify-center">
                            <div className="flex flex-wrap">
                                <div className="w-36 m-2">
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        disabled={
                                            isWithdrawing ||
                                            transactionId !== null
                                        }
                                        onClick={() => withdraw()}
                                    >
                                        Withdraw all
                                    </Button>
                                </div>
                                <div className="w-36 m-2">
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        color="error"
                                        onClick={() => {
                                            close()
                                        }}
                                    >
                                        Close
                                    </Button>
                                </div>
                            </div>
                        </div>
                        {isWithdrawing ? <LinearProgress /> : null}
                    </div>
                ) : null}
                <Collapse
                    in={
                        isWithdrawing ||
                        transactionId !== null ||
                        error !== null
                    }
                >
                    {isWithdrawing ? <LinearProgress /> : null}
                    {transactionId !== null ? (
                        <Alert severity="success">
                            <AlertTitle>Success!</AlertTitle>Balance
                            successfully withdrawn. Transaction ID:{' '}
                            <a
                                href={
                                    (process.env.NEXT_PUBLIC_TESTNET === 'TRUE'
                                        ? 'https://testnet.waxblock.io/transaction/'
                                        : 'https://waxblock.io/transaction/') +
                                    transactionId
                                }
                                target="_blank"
                            >
                                {transactionId}
                            </a>
                        </Alert>
                    ) : null}
                    {error !== null ? (
                        <Alert severity="error">
                            <AlertTitle>An error occured</AlertTitle>
                            {error}
                        </Alert>
                    ) : null}
                </Collapse>
            </DropPaper>
        </Dialog>
    )
}
